import React, { useState } from 'react';
import useLang from '../../i18n';
import { ChevronLeft, ChevronRight, Search, Loader2, AlertTriangle, X, Check, FileText, FileSpreadsheet, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { fmtNum } from '../../utils/dateUtils';

// ── نمایش دوگانه ارز — هر مبلغ فقط با سمبول واقعی خودش، بدون تبدیل ─
// افغانی برجسته/بولد است؛ دالر (اگر واقعاً تراکنشی به دالر وجود داشته باشد) کوچک‌تر نمایش داده می‌شود.
// اگر یکی از دو ارز هیچ تراکنشی نداشته باشد (صفر)، اصلاً نمایش داده نمی‌شود.
export const MoneyDual = ({ afn = 0, usd = 0, size = 'lg', className = '', color = 'text-gray-900' }) => {
  const sizes = {
    lg: { afn: 'text-2xl', usd: 'text-sm' },
    md: { afn: 'text-lg',  usd: 'text-xs' },
    sm: { afn: 'text-sm',  usd: 'text-[11px]' },
  };
  const s = sizes[size] || sizes.lg;
  const hasAfn = Math.round(afn || 0) !== 0;
  const hasUsd = Math.round(usd || 0) !== 0;
  if (!hasAfn && !hasUsd) {
    return <span className={`${s.afn} font-bold ${color} ${className}`}>AFN 0</span>;
  }
  return (
    <div className={`flex items-baseline gap-2 flex-wrap ${className}`}>
      {hasAfn && <span className={`${s.afn} font-bold ${color}`}>AFN {fmtNum(Math.round(afn || 0))}</span>}
      {hasUsd && <span className={`${s.usd} font-semibold text-gray-400`}>${fmtNum(Math.round(usd || 0))}</span>}
    </div>
  );
};

// ── Button ────────────────────────────────────────────────
export const Button = ({ children, variant='default', size='md', loading, icon, className, ...props }) => {
  const base = 'inline-flex items-center gap-2 font-semibold rounded-xl border transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    default: 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300',
    primary: 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700',
    success: 'bg-emerald-700 text-white border-emerald-700 hover:bg-emerald-800',
    danger:  'bg-red-600 text-white border-red-600 hover:bg-red-700',
    ghost:   'bg-transparent text-gray-600 border-transparent hover:bg-gray-100',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} disabled={loading} {...props}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      {children}
    </button>
  );
};

// ── Input ─────────────────────────────────────────────────
export const Input = React.forwardRef(({ label, error, hint, className, ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <input ref={ref}
      className={clsx(
        'w-full px-4 py-2.5 text-sm border rounded-xl bg-white text-gray-900 outline-none transition-all',
        'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
        error && 'border-red-400 focus:border-red-400 focus:ring-red-100',
        className
      )} {...props} />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
));

// ── Select ────────────────────────────────────────────────
export const Select = React.forwardRef(({ label, error, options=[], placeholder, className, ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <select ref={ref}
      className={clsx(
        'w-full px-4 py-2.5 text-sm border rounded-xl bg-white text-gray-900 outline-none transition-all',
        'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
        error && 'border-red-400',
        className
      )} {...props}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
));

// ── Textarea ──────────────────────────────────────────────
export const Textarea = React.forwardRef(({ label, error, rows=3, className, ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <textarea ref={ref} rows={rows}
      className={clsx(
        'w-full px-4 py-2.5 text-sm border rounded-xl bg-white text-gray-900 outline-none transition-all resize-none',
        'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
        error && 'border-red-400',
        className
      )} {...props} />
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
));

// ── CurrencyInput ─────────────────────────────────────────
export const CurrencyInput = React.forwardRef(({ label, error, currency='AFN', ...props }, ref) => (
  <div>
    {label && <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
    <div className="relative">
      <input ref={ref} type="number" min="0" step="any"
        className={clsx(
          'w-full pl-16 pr-4 py-2.5 text-sm border rounded-xl bg-white outline-none transition-all',
          'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
          error && 'border-red-400'
        )} {...props} />
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
        {currency === 'AFN' ? 'AFN' : '$'}
      </span>
    </div>
    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
));

// ── Card ──────────────────────────────────────────────────
export const Card = ({ children, className, ...props }) => (
  <div className={clsx('bg-white border border-gray-200 rounded-lg shadow-sm', className)} {...props}>
    {children}
  </div>
);

// ── Badge ─────────────────────────────────────────────────
const BADGE = {
  green: 'bg-green-100 text-green-700',
  red:   'bg-red-100 text-red-600',
  amber: 'bg-amber-100 text-amber-700',
  blue:  'bg-blue-100 text-blue-700',
  gray:  'bg-gray-100 text-gray-600',
};
export const Badge = ({ label, color='gray', dot }) => (
  <span className={clsx('inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold', BADGE[color])}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
    {label}
  </span>
);

const STATUS_MAP = {
  approved:'green', active:'green', paid:'green', confirmed:'green', settled:'green',
  pending:'amber', partial:'amber', overdue:'amber', draft:'amber',
  cancelled:'red', inactive:'red', rejected:'red',
  transfer:'gray', cash:'blue', credit:'amber',
};
export const StatusBadge = ({ status }) => (
  <Badge label={status} color={STATUS_MAP[status]||'gray'} dot />
);

// ── SearchBox ─────────────────────────────────────────────
export const SearchBox = ({ value, onChange, placeholder='' }) => (
  <div className="relative">
    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-56 pr-10 pl-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 outline-none focus:bg-white focus:border-blue-500 focus:w-72 transition-all" />
  </div>
);

// ── Pagination ────────────────────────────────────────────
export const Pagination = ({ pagination, onChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages } = pagination;
  const pages = Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1);
  return (
    <div className="flex items-center justify-center gap-1.5 pt-4 pb-2">
      <button onClick={() => onChange(page-1)} disabled={!pagination.hasPrev}
        className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-40 hover:bg-gray-50 transition-colors">
        <ChevronRight className="w-4 h-4" />
      </button>
      {pages.map(p => (
        <button key={p} onClick={() => onChange(p)}
          className={clsx('w-9 h-9 rounded-xl text-sm font-semibold transition-all',
            p===page ? 'bg-blue-600 text-white' : 'border border-gray-200 hover:bg-gray-50 text-gray-600')}>
          {p}
        </button>
      ))}
      <button onClick={() => onChange(page+1)} disabled={!pagination.hasNext}
        className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center disabled:opacity-40 hover:bg-gray-50 transition-colors">
        <ChevronLeft className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── Modal ─────────────────────────────────────────────────
export const Modal = ({ isOpen, onClose, title, children, size='md' }) => {
  if (!isOpen) return null;
  const sizes = { sm:'max-w-sm', md:'max-w-lg', lg:'max-w-2xl', xl:'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={clsx('bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto', sizes[size])} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// ── ConfirmDeleteModal ────────────────────────────────────
export const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, loading, itemName }) => {
  const { t } = useLang();
  const [reason, setReason] = useState('');
  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('deleteConfirm')} size="sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-500" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-900">{t('confirm')}</p>
          <p className="text-sm text-gray-500 mt-0.5">{itemName||'این رکورد'} حذف خواهد شد.</p>
        </div>
      </div>
      <Textarea label="دلیل حذف *" rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="دلیل حذف را بنویسید..." />
      <div className="flex gap-2 justify-end mt-4">
        <Button onClick={onClose}>انصراف</Button>
        <Button variant="danger" loading={loading} disabled={!reason.trim()} onClick={() => onConfirm(reason)}>
          <AlertTriangle className="w-4 h-4" /> حذف
        </Button>
      </div>
    </Modal>
  );
};

// ── PageHeader ────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between mb-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

// ── Breadcrumb ────────────────────────────────────────────
export const Breadcrumb = ({ items }) => (
  <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="text-gray-300">›</span>}
        <span className={i===items.length-1 ? 'text-gray-600 font-medium' : 'hover:text-blue-600 cursor-pointer'}>{item}</span>
      </React.Fragment>
    ))}
  </div>
);

// ── FormRow ───────────────────────────────────────────────
export const FormRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

// ── LoadingSpinner ────────────────────────────────────────
export const LoadingSpinner = ({ full }) => (
  <div className={clsx('flex items-center justify-center', full && 'min-h-[200px]')}>
    <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
  </div>
);

// ── EmptyState ────────────────────────────────────────────
export const EmptyState = ({ icon:Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center">
    {Icon && <Icon className="w-14 h-14 text-gray-200 mb-3" />}
    <p className="text-gray-500 font-semibold text-sm">{title||t('noData')}</p>
    {description && <p className="text-gray-400 text-xs mt-1">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// ── Default (auto-colored) cell renderer ─────────────────
// وقتی ستون render مخصوص نداشته باشد، اعداد به‌صورت خودکار رنگی نمایش داده می‌شوند
// (نه سیاه‌وسفید) تا در همه‌ی جدول‌های سیستم رعایت شود.
const numericFromString = (v) => {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!/^-?\d[\d,]*(\.\d+)?$/.test(s)) return null;
  const n = parseFloat(s.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};
const DefaultCell = ({ value }) => {
  if (value === undefined || value === null || value === '') return <span className="text-gray-300">—</span>;
  const n = typeof value === 'number' ? value : numericFromString(value);
  if (n !== null) {
    return <span className={clsx('font-bold', n < 0 ? 'text-red-600' : 'text-blue-700')}>{fmtNum(n)}</span>;
  }
  return <span>{String(value)}</span>;
};

// ── Export helpers (PDF چاپ / Excel) ─────────────────────
const rawCellText = (col, row) => {
  const v = row[col.key];
  if (v === undefined || v === null) return '';
  if (typeof v === 'object') return '';
  return String(v);
};

export const exportTableToExcel = (columns, data, filename = 'export') => {
  const headers = columns.map(c => `<th style="background:#f1f5f9;border:1px solid #cbd5e1;padding:8px;font-weight:bold;">${c.label}</th>`).join('');
  const rows = data.map(row =>
    `<tr>${columns.map(c => `<td style="border:1px solid #e2e8f0;padding:8px;">${rawCellText(c, row)}</td>`).join('')}</tr>`
  ).join('');
  const html = `<html><head><meta charset="utf-8"/></head><body dir="rtl"><table>` +
    `<thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
  const blob = new Blob(['\ufeff' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const exportTableToPDF = (columns, data, filename = 'export', title = '') => {
  const win = window.open('', '_blank');
  if (!win) return;
  const headers = columns.map(c => `<th>${c.label}</th>`).join('');
  const rows = data.map(row =>
    `<tr>${columns.map(c => `<td>${rawCellText(c, row)}</td>`).join('')}</tr>`
  ).join('');
  win.document.write(`<html dir="rtl"><head><meta charset="utf-8"/><title>${title || filename}</title>
    <style>
      body { font-family:'Vazirmatn','Segoe UI',sans-serif; padding:24px; direction:rtl; color:#1e293b; }
      h2 { margin-bottom:16px; }
      table { width:100%; border-collapse:collapse; font-size:13px; }
      th, td { border:1px solid #cbd5e1; padding:8px 10px; text-align:right; }
      th { background:#f1f5f9; font-weight:bold; }
      tr:nth-child(even) td { background:#f8fafc; }
    </style></head>
    <body>${title ? `<h2>${title}</h2>` : ''}
      <table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
};

// ── DataTable ─────────────────────────────────────────────
// جدول با گرید کامل (خط‌های ردیف و ستون جدا)، اعداد رنگی، و نوار ابزار
// (سرچ / فیلتر ستون‌ها / Excel / PDF) بالای جدول.
// searchValue/onSearchChange اختیاری‌اند: اگر صفحه سرچ سمت سرور خودش را دارد
// همان را کنترل می‌کنند (به‌جای سرچ محلی جدا)، در غیر این‌صورت سرچ محلی روی
// داده‌ی همان صفحه انجام می‌شود.
export const DataTable = ({ columns, data, loading, emptyTitle, emptyIcon, actions, pagination, onPageChange, toolbar = true, exportTitle = '', searchValue, onSearchChange, searchPlaceholder, onRowClick }) => {
  const { t } = useLang();
  const [localQuery, setLocalQuery] = useState('');
  const [hidden, setHidden] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const isControlled = typeof onSearchChange === 'function';
  const query = isControlled ? (searchValue || '') : localQuery;
  const setQuery = isControlled ? onSearchChange : setLocalQuery;

  const visibleColumns = columns.filter(c => !hidden.includes(c.key));
  const rows = data || [];
  const filteredData = React.useMemo(() => {
    if (isControlled || !localQuery.trim()) return rows;
    const q = localQuery.trim().toLowerCase();
    return rows.filter(row => columns.some(c => String(row[c.key] ?? '').toLowerCase().includes(q)));
  }, [rows, localQuery, columns, isControlled]);

  const toggleCol = (key) => setHidden(h => h.includes(key) ? h.filter(k => k !== key) : [...h, key]);
  const colCount = visibleColumns.length + (actions ? 1 : 0);

  return (
    <div>
      {toolbar && (
        <div className="flex items-center gap-2 p-2.5 border-b border-gray-100 flex-wrap">
          {/* سرچ اول در DOM قرار دارد تا در چیدمان راست‌به‌چپ، سمت راست (کنار جدول) بیفتد
              و PDF/Excel/فیلتر در سمت چپ آن باشند، مطابق چیدمان درخواستی. */}
          <div className="relative flex-1 min-w-[160px] order-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder || t('phSearch')}
              className="w-full pr-9 pl-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
          </div>

          <div className="relative flex-shrink-0 order-2">
            <button onClick={() => setFilterOpen(v => !v)}
              className={clsx('flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-bold transition-colors',
                (filterOpen || hidden.length) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300')}>
              <SlidersHorizontal className="w-4 h-4" /> {t('filter')}
              {hidden.length > 0 && (
                <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{hidden.length}</span>
              )}
            </button>
            {filterOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
                <div className="absolute left-0 z-20 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-2 max-h-72 overflow-y-auto">
                  <p className="text-[11px] text-gray-400 px-2 py-1 font-semibold">{t('filter')}</p>
                  {columns.map(c => (
                    <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 rounded-md hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={!hidden.includes(c.key)} onChange={() => toggleCol(c.key)}
                        className="w-3.5 h-3.5 accent-blue-600" />
                      {c.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <button onClick={() => exportTableToExcel(visibleColumns, filteredData, exportTitle || 'report')}
            className="order-3 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm font-bold hover:bg-green-100 transition-colors flex-shrink-0">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>

          <button onClick={() => exportTableToPDF(visibleColumns, filteredData, exportTitle || 'report', exportTitle)}
            className="order-4 flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm font-bold hover:bg-red-100 transition-colors flex-shrink-0">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="divide-x divide-gray-200">
              {visibleColumns.map(col => (
                <th key={col.key} className="bg-gray-50 text-gray-600 font-extrabold px-3.5 py-2.5 text-right text-xs border-b-2 border-gray-200 whitespace-nowrap">
                  {col.label}
                </th>
              ))}
              {actions && <th className="bg-gray-50 text-gray-600 font-extrabold px-3.5 py-2.5 text-right text-xs border-b-2 border-gray-200 whitespace-nowrap">{t('actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="py-12"><LoadingSpinner full /></td></tr>
            ) : filteredData.length===0 ? (
              <tr><td colSpan={colCount}>
                <EmptyState icon={emptyIcon} title={emptyTitle} />
              </td></tr>
            ) : filteredData.map((row, i) => (
              <tr key={row._id||i} className={clsx('divide-x divide-gray-100 border-b border-gray-100 hover:bg-blue-50/40 transition-colors', onRowClick && 'cursor-pointer')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {visibleColumns.map(col => (
                  <td key={col.key} className="px-3.5 py-2 text-gray-700 text-sm">
                    {col.render ? col.render(row[col.key], row) : <DefaultCell value={row[col.key]} />}
                  </td>
                ))}
                {actions && <td className="px-3.5 py-2" onClick={e => e.stopPropagation()}>{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden">
        {loading ? (
          <div className="py-12"><LoadingSpinner full /></div>
        ) : filteredData.length===0 ? (
          <EmptyState icon={emptyIcon} title={emptyTitle} />
        ) : filteredData.map((row, i) => (
          <div key={row._id||i} className="border-b border-gray-100 px-4 py-3 hover:bg-gray-50/60">
            {/* First column as title */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex-1 min-w-0">
                {visibleColumns[0] && (
                  <div className="text-sm font-bold text-gray-900">
                    {visibleColumns[0].render ? visibleColumns[0].render(row[visibleColumns[0].key], row) : <DefaultCell value={row[visibleColumns[0].key]} />}
                  </div>
                )}
                {visibleColumns[1] && (
                  <div className="text-xs text-gray-400 mt-0.5">
                    {visibleColumns[1].render ? visibleColumns[1].render(row[visibleColumns[1].key], row) : (row[visibleColumns[1].key] ?? '—')}
                  </div>
                )}
              </div>
              {actions && (
                <div className="flex-shrink-0">{actions(row)}</div>
              )}
            </div>
            {/* Rest of columns as key-value pairs */}
            <div className="grid grid-cols-2 gap-2">
              {visibleColumns.slice(2).map(col => (
                <div key={col.key} className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-gray-400 mb-0.5">{col.label}</p>
                  <div className="text-xs font-bold text-gray-700">
                    {col.render ? col.render(row[col.key], row) : <DefaultCell value={row[col.key]} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {pagination && <Pagination pagination={pagination} onChange={onPageChange} />}
    </div>
  );
};
